/**
 * KST(한국 표준시) 기준 날짜 범위 유틸
 * 영업 마감 19:00 KST 기준으로 기간 계산
 */

const CLOSE_HOUR_UTC = 10; // KST 19:00 = UTC 10:00

/**
 * KST 기준 오늘 날짜의 년/월/일 추출
 */
function getTodayKST(): { year: number; month: number; date: number } {
  const str = new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
  const [year, month, date] = str.split('-').map(Number);
  return { year, month: month - 1, date };
}

/**
 * 당일 19:00 KST(마감시간) 기준으로 지난 7일 범위 반환
 * orderedAt 필터용 UTC Date 객체
 *
 * @returns { from, to } - [from, to] 구간 (이상, 이하)
 */
export function getLastWeekRangeKST(): { from: Date; to: Date } {
  const { year, month, date } = getTodayKST();
  const to = new Date(Date.UTC(year, month, date, CLOSE_HOUR_UTC, 0, 0, 0));
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - 7);
  return { from, to };
}

/**
 * 금일(오늘) KST 00:00 ~ 23:59:59.999 범위 반환
 * orderedAt 필터용 Date 객체
 *
 * @returns { from, to } - [from, to] 구간 (이상, 이하)
 */
export function getTodayRangeKST(): { from: Date; to: Date } {
  const { year, month, date } = getTodayKST();
  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
  const from = new Date(`${dateStr}T00:00:00+09:00`);
  const to = new Date(`${dateStr}T23:59:59.999+09:00`);
  return { from, to };
}

/**
 * 금일 KST 날짜 문자열 (YYYY-MM-DD)
 */
export function getTodayDateStringKST(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
}

/**
 * 지난 7일 KST 날짜 범위 (startDate, endDate) YYYY-MM-DD
 */
export function getLastWeekDateRangeKST(): { startDate: string; endDate: string } {
  const today = getTodayDateStringKST();
  return getDateRangeForBaseDate(today);
}

/**
 * baseDate가 속한 주(월~일)의 월요일 기준으로, 최근 count개의 주간(월~일) 범위 배열 반환
 * @param baseDate YYYY-MM-DD
 * @param count 반환할 주 수
 */
export function getWeekPeriodsForBaseDate(
  baseDate: string,
  count: number
): { startDate: string; endDate: string }[] {
  const [y, m, d] = baseDate.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const day = date.getDay(); // 0=일, 1=월 ...
  const diff = day === 0 ? -6 : 1 - day; // 해당 주 월요일까지의 offset
  const monday = new Date(date);
  monday.setDate(date.getDate() + diff);

  const fmt = (dt: Date) => dt.toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
  const periods: { startDate: string; endDate: string }[] = [];

  for (let i = count - 1; i >= 0; i--) {
    const start = new Date(monday);
    start.setDate(monday.getDate() - i * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    periods.push({ startDate: fmt(start), endDate: fmt(end) });
  }

  return periods;
}

/**
 * 기준일(baseDate) 포함 직전 7일 범위 반환
 * @param baseDate YYYY-MM-DD
 */
export function getDateRangeForBaseDate(baseDate: string): { startDate: string; endDate: string } {
  const [year, month, date] = baseDate.split('-').map(Number);
  const endDate = new Date(year, month - 1, date);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - 6);
  const fmt = (d: Date) => d.toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
  return { startDate: fmt(startDate), endDate: fmt(endDate) };
}

/**
 * 기준일이 속한 주(월~일) 범위 반환
 * @param baseDate YYYY-MM-DD
 */
export function getWeekRangeForDate(baseDate: string): { startDate: string; endDate: string } {
  const [y, m, d] = baseDate.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const day = date.getDay(); // 0=일, 1=월 ...
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(date);
  monday.setDate(date.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (dt: Date) => dt.toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
  return { startDate: fmt(monday), endDate: fmt(sunday) };
}

/**
 * 기준일이 속한 월(1일~말일) 범위 반환
 * @param baseDate YYYY-MM-DD
 */
export function getMonthRangeForDate(baseDate: string): { startDate: string; endDate: string } {
  const [y, m] = baseDate.split('-').map(Number);
  const firstDay = new Date(y, m - 1, 1);
  const lastDay = new Date(y, m, 0); // 다음 달 0일 = 이번 달 말일
  const fmt = (dt: Date) => dt.toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
  return { startDate: fmt(firstDay), endDate: fmt(lastDay) };
}
